import React, { useCallback, useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import ContentLayout from "Components/ContentLayout";
import EnvironmentTree from "Components/EnvironmentTree";
import ErrorBoundary from "Components/ErrorBoundary";
import ListGroup from "Components/ListGroup";
import PageMeta from "Components/PageMeta";
import ProjectMoreLinks from "Components/ProjectMoreLinks";
import ResourcesBreakdown from "Components/ResourcesBreakdown";
import useMediaQuery from "Hooks/useMediaQuery";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { isDg3 } from "Libs/utils";
import { getSelectors } from "Reducers/activity";
import { meSelector } from "Reducers/app/selectors";
import { environmentsAsArraySelector } from "Reducers/environment";
import { deploymentSizingApiEnabledSelector } from "Reducers/environment/deployment";
import { selectHasDedicatedDeployment } from "Reducers/environment/environment.selectors";
import { getFeatureFlag } from "Reducers/featureFlags";
import {
  selectFeatureServiceTreeEnabled,
  selectProjectProvisioningWizardEnabled,
  selectProjectTrafficBlockEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { selectSubscriptionDetails as organizationSubscriptionSelector } from "Reducers/organization/subscription";
import {
  gitProjectSelector,
  getIsLoadingProjects
} from "Reducers/project/project";
import {
  loadProjectWizard,
  toggleProjectWizard
} from "Reducers/project/wizard";
import { setProjectContext } from "Reducers/settingsMenu";
import { subscriptionSelector } from "Reducers/subscription";
import ServicesDisplaySkeleton from "src/organization/common/components/ServicesDisplaySkeleton";
import ActivitySection from "src/organization/common/containers/ActivitySection/ActivitySection";
import InfoDropButtons from "src/organization/common/containers/InfoDropButtons";
import ServicesDisplay from "src/organization/common/containers/ServicesDisplay";
import PluginApp from "src/organization/pages/project/app/Page";
import {
  ProjectDetails,
  MonthlyTraffic
} from "src/organization/pages/project/components";
import { usePluginApps } from "src/root/pages/pluginApps/usePluginApps";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import ContentWizard from "../ContentWizard";
import InitializeWizard from "../InitializeWizard";

import { useShowResourcesSticker } from "./hooks/useShowResourcesSticker";
import {
  LiveButton,
  ButtonsContainer,
  ColumnWrapper,
  Flex,
  BoxLayout,
  RowLayout,
  SettingsButton,
  SettingsIcon,
  CodeContainer,
  Title,
  EnvironmentName
} from "./ProjectOverview.styles";
import ProjectOverviewSkeleton from "./ProjectOverviewSkeleton";

const ProjectOverview = ({ organizationId, projectId }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const showResourcesSticker = useShowResourcesSticker();

  const [pluginApps] = usePluginApps();
  const [mainEnvironment, setMainEnv] = useState();
  const [areServicesReady, setAreServicesReady] = useState(false);

  const dedicatedDeployment = useSelectorWithUrlParams(
    selectHasDedicatedDeployment
  );

  const user = useAppSelector(meSelector);

  const project = useAppSelector(state =>
    gitProjectSelector(state, { organizationId, projectId })
  );

  const isLoadingProject = useAppSelector(getIsLoadingProjects);

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  const projectProvisioningWizardEnabled = useAppSelector(
    selectProjectProvisioningWizardEnabled
  );
  const projectTrafficBlockEnabled = useAppSelector(
    selectProjectTrafficBlockEnabled
  );

  const subscription = useAppSelector(state => {
    return getFeatureFlag("ENABLE_ORGANIZATION")
      ? organizationSubscriptionSelector(state, {
          organizationId,
          subscriptionId: project?.getSubscriptionId()
        })
      : subscriptionSelector(state, {
          organizationId,
          projectId,
          id: project?.getSubscriptionId()
        });
  });

  const { all } = getSelectors("project", {
    projectId,
    organizationId
  });

  const activitiesLength = Object.keys(useAppSelector(all)).length;

  const isSizingApiEnabled = useSelectorWithUrlParams(
    deploymentSizingApiEnabledSelector,
    { environmentId: project?.default_branch ?? "" }
  );

  const enableServicesReady = useCallback(() => {
    setAreServicesReady(true);
  }, []);

  useEffect(() => {
    let mainEnv = "";
    if (environments) {
      mainEnv = environments.find(env => env.is_main);
    }
    setMainEnv(mainEnv);
  }, [environments?.length]);

  useEffect(() => {
    if (!project?.id || !subscription?.id) return;
    dispatch(
      loadProjectWizard({
        user,
        organizationId,
        projectId,
        hasCode,
        intl
      })
    );
  }, [subscription?.id, activitiesLength, project?.id]);

  const hasCode = useMemo(
    () =>
      typeof mainEnvironment?.hasPermission === "function" &&
      mainEnvironment.hasPermission("#initialize"),
    [mainEnvironment]
  );

  const onMobile = useMediaQuery("(max-width: 768px)");

  const settingsLink = `/${organizationId}/${projectId}/-/settings`;

  const handleSettingsClicked = e => {
    e.stopPropagation();
    dispatch(setProjectContext());
    navigate(settingsLink);
  };

  const toggleWizard = () => {
    dispatch(toggleProjectWizard({ organizationId, projectId }));
  };

  let buttonContainer = null;
  if (project) {
    buttonContainer = onMobile ? (
      <ButtonsContainer>
        <Flex>
          <LiveButton
            project={project}
            subscription={subscription}
            organizationId={organizationId}
          />
        </Flex>
        <Flex>
          <SettingsButton aria-label="Settings" onClick={handleSettingsClicked}>
            <SettingsIcon />
          </SettingsButton>
          <ProjectMoreLinks
            iconColor="granite"
            from="project"
            project={project}
            subscription={subscription}
            toggleProjectWizard={toggleWizard}
          />
        </Flex>
      </ButtonsContainer>
    ) : (
      <CodeContainer>
        <InfoDropButtons className="buttons" project={project} />
      </CodeContainer>
    );
  }

  const serviceTreeEnabled = useAppSelector(selectFeatureServiceTreeEnabled);

  const isDg3Project = isDg3(subscription?.enterprise_tag, dedicatedDeployment);

  const serviceTreeOrDg3 = serviceTreeEnabled || isDg3Project;

  return (
    <ContentLayout style={{ marginTop: "16px" }}>
      <PageMeta title={project?.title} />
      <ErrorBoundary>
        {buttonContainer}
        <ColumnWrapper>
          <aside className="col-4">
            <ListGroup
              className="project-info"
              aria-labelledby="project-heading"
            >
              {isLoadingProject ? (
                <ProjectOverviewSkeleton />
              ) : (
                <BoxLayout>{project && <ProjectDetails />}</BoxLayout>
              )}
            </ListGroup>

            {projectTrafficBlockEnabled && (
              <ListGroup>
                <MonthlyTraffic subscription={subscription} />
              </ListGroup>
            )}

            {Object.entries(pluginApps)
              .filter(([, plugin]) =>
                plugin.hooks?.some(
                  hook =>
                    hook.route === "/:organizationId/:projectId" &&
                    hook.type === "overview"
                )
              )
              .map(([appId]) => {
                return (
                  <ListGroup key={appId}>
                    <PluginApp
                      applicationId={appId}
                      route="/:organizationId/:projectId"
                      type="overview"
                      organizationId={organizationId}
                      projectId={projectId}
                    />
                  </ListGroup>
                );
              })}
            {isSizingApiEnabled && <ResourcesBreakdown />}

            {serviceTreeOrDg3 && (
              <ListGroup aria-labelledby="services-heading">
                {!areServicesReady && <ServicesDisplaySkeleton />}
                <BoxLayout className="services">
                  <Title tag="h2">
                    {intl.formatMessage({ id: "apps_services" })}
                  </Title>
                  {mainEnvironment?.title && (
                    <EnvironmentName weight="semiBold">
                      {mainEnvironment.title}
                    </EnvironmentName>
                  )}

                  <RowLayout>
                    <ServicesDisplay
                      height="250px"
                      maxHeight={220}
                      mainEnvironmentId={mainEnvironment?.id}
                      display="project"
                      treePositionY={10}
                      hasInfoTooltip
                      projectId={projectId}
                      organizationId={organizationId}
                      onLoadEnd={enableServicesReady}
                    />
                  </RowLayout>
                </BoxLayout>
              </ListGroup>
            )}
          </aside>

          <div>
            {projectProvisioningWizardEnabled && <InitializeWizard />}

            <ContentWizard
              subscription={subscription}
              project={project}
              user={user}
            />

            <EnvironmentTree showResourcesSticker={showResourcesSticker} />

            <ActivitySection
              context="project"
              excludeActivies={["filter.integration"]}
            />
          </div>
        </ColumnWrapper>
      </ErrorBoundary>
    </ContentLayout>
  );
};

ProjectOverview.propTypes = {
  projectId: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired
};

export default ProjectOverview;
